import { semantic } from '@profylr/design-tokens'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { useDocumentTheme, type DocumentTheme } from '@/hooks/useDocumentTheme'

const { light, dark } = semantic

/**
 * One chrome palette. Every value is a design-system token, never a literal.
 *
 * The six entries are exactly the chrome tokens NavShell reads. All six are
 * supplied together, because overriding a subset is what made the rail and the
 * panel disagree: the rail draws on `surface-nav-deep` and the panel on
 * `surface-nav`, so a preset that set only the latter produced a white panel
 * hinged to a dark rail.
 */
interface ChromePreset {
  /** --profylr-surface-nav: the panel, and the shell behind the rail. */
  background: string
  /** --profylr-surface-nav-deep: the rail itself. */
  backgroundDeep: string
  /** --profylr-text-nav, and its two quieter steps. */
  foreground: string
  foregroundSecondary: string
  foregroundMuted: string
  /** --profylr-border-nav-subtle. */
  border: string
}

export type SidebarTheme = 'original' | 'dark' | 'white'

const STORAGE_KEY = 'sidebar-theme'

// Chrome presets.
//
// OR-546 introduced these. They now resolve against the active theme rather
// than reading the light palette in every case, and they are scoped to the
// sidebar rather than written onto the root element.
//
// Each preset selects from the design system's semantic tokens, so contrast is
// gated once in @profylr/design-tokens rather than re-argued here. The former
// "original" green is exactly what the design system removes: the brand is
// purple, and purple now carries navigation and chrome.
function chromeFor(preset: SidebarTheme, documentTheme: DocumentTheme): ChromePreset {
  const palette = documentTheme === 'dark' ? dark : light

  switch (preset) {
    /*
     * Brand purple chrome. The default, and the design system's intent.
     *
     * It follows the active theme, which is the whole fix: writing the light
     * palette here unconditionally meant dark mode drew its chrome from the
     * light theme and the toggle only half worked.
     */
    case 'original':
      return {
        background: palette['surface-nav'],
        backgroundDeep: palette['surface-nav-deep'],
        foreground: palette['text-nav'],
        foregroundSecondary: palette['text-nav-secondary'],
        foregroundMuted: palette['text-nav-muted'],
        border: palette['border-nav-subtle'],
      }

    /*
     * Chrome darker than the canvas, whichever canvas is showing. Deliberately
     * not theme-following: choosing it is choosing the dark chrome.
     */
    case 'dark':
      return {
        background: dark['surface-nav'],
        backgroundDeep: dark['surface-nav-deep'],
        foreground: dark['text-nav'],
        foregroundSecondary: dark['text-nav-secondary'],
        foregroundMuted: dark['text-nav-muted'],
        border: dark['border-nav-subtle'],
      }

    /*
     * Chrome recedes into the working canvas, for users who would rather the
     * rail did not announce itself.
     *
     * "White" is its light-theme appearance rather than a literal instruction:
     * in dark mode receding means taking the dark canvas, not painting a white
     * slab beside it. The nav text steps have to come from the canvas set too,
     * because text-nav* are near-white in *both* themes -- they are drawn for
     * purple chrome, and on a white surface they are invisible.
     */
    case 'white':
      return {
        background: palette.surface,
        backgroundDeep: palette['surface-sunken'],
        foreground: palette.text,
        foregroundSecondary: palette.text,
        foregroundMuted: palette['text-muted'],
        border: palette.divider,
      }
  }
}

/**
 * The preset as CSS custom properties, for the element that wraps the sidebar.
 *
 * Applied there and not to `document.documentElement`, which is where this
 * used to write them. An inline style on the root beats every stylesheet rule,
 * so the preset leaked out of the sidebar and repainted anything else built
 * from the same tokens -- most visibly `.gradient-auth`, whose background and
 * mid-gradient stop both read `--profylr-surface-nav`, which is how the
 * "white" preset put a white band through the sign-in screen.
 */
function chromeStyleFor(chrome: ChromePreset): React.CSSProperties {
  return {
    '--profylr-surface-nav': chrome.background,
    '--profylr-surface-nav-deep': chrome.backgroundDeep,
    '--profylr-text-nav': chrome.foreground,
    '--profylr-text-nav-secondary': chrome.foregroundSecondary,
    '--profylr-text-nav-muted': chrome.foregroundMuted,
    '--profylr-border-nav-subtle': chrome.border,
  } as React.CSSProperties
}

interface SidebarThemeContextType {
  theme: SidebarTheme
  setTheme: (theme: SidebarTheme) => void
  /** Spread onto the element that encloses the sidebar. */
  chromeStyle: React.CSSProperties
}

const SidebarThemeContext = createContext<SidebarThemeContextType | undefined>(undefined)

interface SidebarThemeProviderProps {
  children: React.ReactNode
}

export function SidebarThemeProvider({ children }: SidebarThemeProviderProps) {
  const [theme, setThemeState] = useState<SidebarTheme>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored === 'original' || stored === 'dark' || stored === 'white') {
        return stored as SidebarTheme
      }
    } catch {
      // Ignore localStorage errors
    }
    return 'original'
  })

  const documentTheme = useDocumentTheme()

  const chromeStyle = useMemo(
    () => chromeStyleFor(chromeFor(theme, documentTheme)),
    [theme, documentTheme],
  )

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      // Ignore
    }
  }, [theme])

  const value = useMemo(
    () => ({ theme, setTheme: setThemeState, chromeStyle }),
    [theme, chromeStyle],
  )

  return (
    <SidebarThemeContext.Provider value={value}>{children}</SidebarThemeContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSidebarTheme(): SidebarThemeContextType {
  const context = useContext(SidebarThemeContext)
  if (context === undefined) {
    throw new Error('useSidebarTheme must be used within a SidebarThemeProvider')
  }
  return context
}

// Exported for the contrast test, which resolves presets the same way the
// provider does rather than re-parsing this file for literals.
// eslint-disable-next-line react-refresh/only-export-components
export { chromeFor }
